import React from 'react';
import { useFilterContext } from '../context/aptmetrix/FilterContext';
import { areFiltersActive } from '../util/aptMetrixFilters';

export default function FilterStatusBanner({ filteredData, aptMetrixInfo }) {
  const {
    priceFilter, householdFilter, areaFilter, gapFilter,
    moveInDateFilter, subdistrictFilter, subwayFilter,
    gangnamTimeFilter, schoolFilter,
  } = useFilterContext();

  const filters = {
    priceFilter, householdFilter, areaFilter, gapFilter,
    moveInDateFilter, subdistrictFilter, subwayFilter,
    gangnamTimeFilter, schoolFilter,
  };

  if (!areFiltersActive(filters, aptMetrixInfo)) return null;

  const activeLabels = [
    (priceFilter.minPrice !== null || priceFilter.maxPrice !== null) && '매매가',
    (householdFilter.minHousehold !== null || householdFilter.maxHousehold !== null) && '세대수',
    (areaFilter.minArea !== null || areaFilter.maxArea !== null) && '전용면적',
    (gapFilter.minGap !== null || gapFilter.maxGap !== null) && '전매갭',
    (moveInDateFilter.minYears !== null || moveInDateFilter.maxYears !== null) && '입주연식',
    subdistrictFilter && subdistrictFilter.length > 0 &&
      subdistrictFilter.length < (aptMetrixInfo ? [...new Set(aptMetrixInfo.map(i => i.subdistrict_name))].filter(Boolean).length : 0) && '동필터',
    subwayFilter !== null && (
      typeof subwayFilter === 'object' && !Array.isArray(subwayFilter)
        ? `지하철(${[subwayFilter.lines?.length && `${subwayFilter.lines.length}노선`, subwayFilter.maxWalkingTime && `${subwayFilter.maxWalkingTime}분`].filter(Boolean).join(',')})`
        : Array.isArray(subwayFilter) ? `노선(${subwayFilter.length}개)` : '지하철'
    ),
    gangnamTimeFilter?.maxTime !== null && `강남 ${gangnamTimeFilter.maxTime}분`,
    schoolFilter !== null && (schoolFilter.hasSchool ? '초등학교O' : '초등학교X'),
  ].filter(Boolean);

  const filterDetails = [
    { active: priceFilter.minPrice !== null || priceFilter.maxPrice !== null, label: '매매가 필터', value: `${priceFilter.minPrice?.toLocaleString() || '0'}만원~ ${priceFilter.maxPrice?.toLocaleString() || '무제한'}만원` },
    { active: householdFilter.minHousehold !== null || householdFilter.maxHousehold !== null, label: '총세대수 필터', value: `${householdFilter.minHousehold || '0'}세대~ ${householdFilter.maxHousehold || '무제한'}세대` },
    { active: areaFilter.minArea !== null || areaFilter.maxArea !== null, label: '전용면적 필터', value: `${areaFilter.minArea || '0'}m²~ ${areaFilter.maxArea || '무제한'}m²` },
    { active: gapFilter.minGap !== null || gapFilter.maxGap !== null, label: '전매갭 필터', value: `${gapFilter.minGap || '0'}만원~ ${gapFilter.maxGap || '무제한'}만원` },
    { active: moveInDateFilter.minYears !== null || moveInDateFilter.maxYears !== null, label: '입주연식 필터', value: `${moveInDateFilter.minYears || '0'}~ ${moveInDateFilter.maxYears || '무제한'}년` },
    { active: gangnamTimeFilter?.maxTime !== null, label: '강남소요시간', value: `${gangnamTimeFilter?.maxTime}분 이내` },
  ];

  const resultCount = filteredData ? filteredData.length : 0;
  const totalCount = aptMetrixInfo ? aptMetrixInfo.length : 0;

  return (
    <div className="flex justify-center w-full">
      <div className="flex flex-col justify-between items-start my-2 p-2 bg-blue-50 border border-blue-200 rounded-md shadow-sm w-full md:w-1/2">
        {/* Mobile */}
        <div className="md:hidden w-full">
          <p className="text-sm text-blue-800 mb-1">
            <span className="font-medium">적용된 필터:</span> {activeLabels.join(', ')}
          </p>
          {filteredData && (
            <p className="text-sm text-blue-800 mt-1">
              <span className="px-2 py-0.5 bg-blue-100 rounded-full text-xs">
                검색 결과: {resultCount}건 / 전체 {totalCount}건
              </span>
            </p>
          )}
        </div>

        {/* Desktop */}
        <div className="hidden md:block">
          {filterDetails.filter(f => f.active).map(f => (
            <p key={f.label} className="text-sm text-blue-800 mb-1">
              <span className="font-medium">{f.label}:</span> {f.value}
            </p>
          ))}

          {subdistrictFilter && subdistrictFilter.length > 0 &&
            subdistrictFilter.length < (aptMetrixInfo ? [...new Set(aptMetrixInfo.map(i => i.subdistrict_name))].filter(Boolean).length : 0) && (
            <p className="text-sm text-blue-800 mb-1">
              <span className="font-medium">동 필터:</span> {subdistrictFilter.join(', ')}
            </p>
          )}

          {subwayFilter !== null && (
            <p className="text-sm text-blue-800 mb-1">
              <span className="font-medium">지하철 필터:</span>{' '}
              {typeof subwayFilter === 'object' && !Array.isArray(subwayFilter) ? (
                <>
                  {subwayFilter.lines?.length > 0 && (
                    <span>{subwayFilter.lines.length > 3
                      ? `${subwayFilter.lines.slice(0, 3).join(', ')} 외 ${subwayFilter.lines.length - 3}개 노선`
                      : subwayFilter.lines.join(', ')}</span>
                  )}
                  {subwayFilter.maxWalkingTime && (
                    <span>{subwayFilter.lines?.length > 0 ? ', ' : ''}도보 {subwayFilter.maxWalkingTime}분 이내</span>
                  )}
                </>
              ) : Array.isArray(subwayFilter) ? (
                subwayFilter.length > 3
                  ? `${subwayFilter.slice(0, 3).join(', ')} 외 ${subwayFilter.length - 3}개 노선`
                  : subwayFilter.join(', ')
              ) : (subwayFilter ? '있음' : '없음')}
            </p>
          )}

          {schoolFilter !== null && (
            <p className="text-sm text-blue-800 mb-1">
              <span className="font-medium">초등학교:</span> {schoolFilter.hasSchool ? '있음' : '없음'}
            </p>
          )}

          {filteredData && (
            <p className="text-sm text-blue-800 mt-1">
              <span className="px-2 py-0.5 bg-blue-100 rounded-full text-xs">
                검색 결과: {resultCount}건 / 전체 {totalCount}건
              </span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
